import type { Audience } from 'cesg-shared/dist/models';
import type { FileManagerMount } from '../core/useFileManagerMounts';

/**
 * What a drop means, once the source and destination mounts are known.
 *
 * `operation` is the verb Graph will be asked for: a move (PATCH parentReference) can only ever
 * stay on one drive, so anything crossing a mount boundary is necessarily a copy.
 */
export interface TransferPolicy {
  operation: 'move' | 'copy';
  /**
   * Audiences the destination can be read by that the source cannot — the *widening* the drop
   * causes. Empty when the drop discloses to nobody new (same mount, or a copy that narrows).
   * Rendered verbatim by the confirmation modal, which is why this is the set and not a boolean.
   */
  gainedAudiences: Audience[];
  requiresConfirmation: boolean;
}

/**
 * What dropping items from `source` into `destination` means.
 *
 * Derived from the mounts' own `audience` — itself derived from `SITE_LIBRARIES[].grants` by
 * `audienceOf()` — and deliberately NOT from a hand-maintained table of library pairs. A pair table
 * would be a claim about the ACL relationship stored away from the ACL, free to drift from it, which
 * is the same reasoning that made `audienceOf` derive from `grants` rather than from a `sensitivity`
 * field (D14/D15). It also means nothing in this feature knows the names `CESG` or `SHARED`: a third
 * library reaching a third audience gets a correct policy, and a correct confirmation sentence, with
 * no change here.
 *
 * The coupling this creates is intended and worth stating out loud: editing a library's `grants`
 * changes which drops prompt the user. Granting guests access to a library IS the thing that should
 * make drops into it disclose.
 */
export const transferPolicyFor = (
  source: FileManagerMount,
  destination: FileManagerMount,
): TransferPolicy => {
  // Compared by `key`, never by `title` (free to change) or `driveId` (an implementation detail of
  // where the mount is backed) — `key` is the code-owned identity the spec guarantees is stable.
  if (source.key === destination.key) {
    return { operation: 'move', gainedAudiences: [], requiresConfirmation: false };
  }

  const gainedAudiences = destination.audience.filter(
    (audience) => !source.audience.includes(audience),
  );

  return {
    operation: 'copy',
    gainedAudiences,
    requiresConfirmation: gainedAudiences.length > 0,
  };
};
